import { computed, Signal } from '@angular/core';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import {
  AccuracyCapableMeasure,
  AccuracyMeasureKey,
  accuracyKeyOf,
  ANALYSIS_MEASURE_ORDER,
  isAccuracyCapable,
  Measure,
} from '@api-types/enums/measure.enum';
import { CoreState } from '@core/store/core.state';
import { selectFeatureFlagById } from '@core/store/feature-flags/feature-flags.selectors';
import { Store } from '@ngrx/store';

export {
  ACCURACY_MEASURES,
  accuracyKeyOf,
  toAccuracyValue,
} from '@api-types/enums/measure.enum';
export type {
  AccuracyCapableMeasure,
  AccuracyMeasureKey,
} from '@api-types/enums/measure.enum';

const CAPPED_MEASURE_FLAGS: Partial<Record<Measure, FeatureFlagId>> = {
  [Measure.CappedWape]: FeatureFlagId.capped_wape,
  [Measure.CappedMape]: FeatureFlagId.capped_mape,
};

const MEASURE_COLUMN_HEADERS: Partial<Record<Measure, string>> = {
  [Measure.CappedWape]: 'Capped WAPE',
  [Measure.CappedMape]: 'Capped MAPE',
};

export const getMeasureColumnHeader = (measure: string): string =>
  MEASURE_COLUMN_HEADERS[measure as Measure] ?? measure.toUpperCase();

export const getMeasureTooltipKey = (measure: Measure): string =>
  `RESULT.MEASURE.EXPLANATION.${measure.toUpperCase()}`;

export type MeasureCategory = {
  labelKey: string;
  measures: Measure[];
};

export const RELATIVE_CATEGORY_LABEL_KEY = 'ANALYSIS.MEASURE_CATEGORY.RELATIVE';
const SCALE_DEPENDENT_CATEGORY_LABEL_KEY =
  'ANALYSIS.MEASURE_CATEGORY.SCALE_DEPENDENT';
const BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY =
  'ANALYSIS.MEASURE_CATEGORY.BENCHMARK_RELATIVE';

type CategoryLabelKey =
  | typeof RELATIVE_CATEGORY_LABEL_KEY
  | typeof SCALE_DEPENDENT_CATEGORY_LABEL_KEY
  | typeof BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY;

const CATEGORY_ORDER: CategoryLabelKey[] = [
  RELATIVE_CATEGORY_LABEL_KEY,
  SCALE_DEPENDENT_CATEGORY_LABEL_KEY,
  BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY,
];

/** Entry order defines the order of the measures inside their category. */
const MEASURE_CATEGORY_LABEL_KEYS: Record<Measure, CategoryLabelKey> = {
  [Measure.Wape]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.CappedWape]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Mape]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.CappedMape]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Smape]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Wpe]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Mpe]: RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Mae]: SCALE_DEPENDENT_CATEGORY_LABEL_KEY,
  [Measure.Me]: SCALE_DEPENDENT_CATEGORY_LABEL_KEY,
  [Measure.Rmse]: SCALE_DEPENDENT_CATEGORY_LABEL_KEY,
  [Measure.Mase]: BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Rrse]: BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY,
  [Measure.Rae]: BENCHMARK_RELATIVE_CATEGORY_LABEL_KEY,
};

export const MEASURE_CATEGORIES: MeasureCategory[] = CATEGORY_ORDER.map(
  (labelKey) => ({
    labelKey,
    measures: (
      Object.entries(MEASURE_CATEGORY_LABEL_KEYS) as [
        Measure,
        CategoryLabelKey,
      ][]
    )
      .filter(([, categoryLabelKey]) => categoryLabelKey === labelKey)
      .map(([measure]) => measure),
  }),
);

export const getAccuracyColumnHeader = (
  measure: AccuracyCapableMeasure,
  accuracySuffix: string,
): string => `${getMeasureColumnHeader(measure)} (${accuracySuffix})`;

export type MeasureSelection = Partial<
  Record<Measure | AccuracyMeasureKey, boolean>
>;

export type MeasureFormat = 'number' | 'percent';

export type SelectedMeasureColumn = {
  colId: string;
  measure: Measure;
  isAccuracy: boolean;
  header: string;
  format: MeasureFormat;
};

/**
 * Error and accuracy variant of the same measure are independent columns; both
 * the analysis table and the KPI chip row render them in this order. Error
 * measures render as their raw value (relative ones between 0 and 1), only the
 * accuracy variant is shown as a percentage.
 */
export const getSelectedMeasureColumns = (
  configuration: MeasureSelection | undefined,
  isMeasureVisible: (measure: string) => boolean,
  accuracySuffix: string,
): SelectedMeasureColumn[] =>
  ANALYSIS_MEASURE_ORDER.filter((measure) => isMeasureVisible(measure)).flatMap(
    (measure) => {
      const columns: SelectedMeasureColumn[] = [];

      if (configuration?.[measure] ?? true) {
        columns.push({
          colId: measure,
          measure,
          isAccuracy: false,
          header: getMeasureColumnHeader(measure),
          format: 'number',
        });
      }

      if (isAccuracyCapable(measure)) {
        const accuracyKey = accuracyKeyOf(measure);
        if (configuration?.[accuracyKey] === true) {
          columns.push({
            colId: accuracyKey,
            measure,
            isAccuracy: true,
            header: getAccuracyColumnHeader(measure, accuracySuffix),
            format: 'percent',
          });
        }
      }

      return columns;
    },
  );

/**
 * Returns a signal-backed predicate telling whether a measure is currently
 * visible. Measures behind a feature flag (capped_wape / capped_mape) are only
 * visible when their flag is enabled; every other measure is always visible.
 * Centralised here so a new flag-gated measure only needs an entry in
 * CAPPED_MEASURE_FLAGS instead of duplicating flag signals in each component.
 */
export const createMeasureVisibility = (
  store: Store<CoreState>,
): ((measure: string) => boolean) => {
  const flagSignals = new Map<FeatureFlagId, Signal<boolean>>();
  for (const flag of Object.values(CAPPED_MEASURE_FLAGS)) {
    if (!flagSignals.has(flag)) {
      const flagState = store.selectSignal(selectFeatureFlagById(flag));
      flagSignals.set(
        flag,
        computed(() => flagState().enabled),
      );
    }
  }

  return (measure: string): boolean => {
    const flag = CAPPED_MEASURE_FLAGS[measure as Measure];
    if (!flag) {
      return true;
    }
    return flagSignals.get(flag)?.() ?? false;
  };
};
